import { Controller, useFormContext } from 'react-hook-form';
import React from 'react';
import { EqualisationSettingsFormValues } from './FormWrapper';
import { PreferredReturnAccrualMethod } from 'further-types/lp/carry-settings';
import { TextField, Typography } from '@mui/material';
import useCarriedInterestFeeTemplatesOptions from '../CarrySettings/CarriedInterestFeeTemplates/hooks/useCarriedInterestFeeTemplatesOptions';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import NumberInput from 'further-ui/components/NumberInput';
import Switch from 'further-ui/components/Switch';
import { Column } from 'further-ui/components/Layout';

const EqualisationInterestFeeTemplateFields: React.FC<{
  index: number;
}> = ({ index }) => {
  const { watch, control, setValue } =
    useFormContext<EqualisationSettingsFormValues>();
  const {
    preferredReturnAccrualMethodOptions:
      equalisationInterestAccrualMethodOptions,
    preferredReturnCompoundingPeriodOptions:
      equalisationInterestCompoundingPeriodOptions,
    preferredReturnBasisOptions: equalisationInterestBasisOptions,
  } = useCarriedInterestFeeTemplatesOptions();

  const isCompoundAccrualMethod =
    watch(
      `equalisationInterestFeeTemplates.${index}.equalisationInterestAccrualMethod`,
    ) === PreferredReturnAccrualMethod.Compound;

  return (
    <Column spacing="lg">
      <FormField
        size="md"
        breakpoint="md"
        labelSize="md"
        layout="horizontal"
        label="Template name"
      >
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.name`}
          control={control}
          render={({ fieldState: { error }, field }) => (
            <TextField
              size="small"
              placeholder="Enter template name"
              {...field}
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        breakpoint="md"
        labelSize="md"
        layout="horizontal"
        label="Default template"
      >
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.isDefault`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Switch
              checked={!!value}
              edge="start"
              onChange={(newValue) => {
                onChange(newValue);
                if (newValue) {
                  const allTemplates = watch(
                    'equalisationInterestFeeTemplates',
                  );
                  allTemplates.forEach((_, idx) => {
                    if (idx !== index) {
                      setValue(
                        `equalisationInterestFeeTemplates.${idx}.isDefault`,
                        false,
                      );
                    }
                  });
                }
              }}
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        breakpoint="md"
        labelSize="md"
        layout="horizontal"
        label="Equalisation interest rate"
        tooltip="This field may be left blank and input at equalisation (in the Close & Equalise feature) if the fund vehicle uses a floating rate (e.g. SONIA), which means the rate will be unknown until the point of equalisation."
      >
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.equalisationInterestRate`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <NumberInput
              fullWidth
              {...rest}
              decimals={10}
              onChange={(value) => {
                onChange(value ?? 0);
              }}
              placeholder="Enter rate"
              error={!!error}
              helperText={error?.message}
              showPercentSuffix
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        breakpoint="md"
        labelSize="md"
        layout="horizontal"
        label="Equalisation interest accrual method"
      >
        <Controller
          name={`equalisationInterestFeeTemplates.${index}.equalisationInterestAccrualMethod`}
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <BasicSelect
              size="small"
              options={equalisationInterestAccrualMethodOptions}
              placeholder="Select method"
              fullWidth
              error={!!error}
              helperText={error?.message}
              value={value}
              onChange={(newValue) => {
                onChange(newValue);
                if (newValue === PreferredReturnAccrualMethod.Simple) {
                  setValue(
                    `equalisationInterestFeeTemplates.${index}.equalisationInterestCompoundingPeriod`,
                    null,
                  );
                }
              }}
            />
          )}
        />
      </FormField>
      {isCompoundAccrualMethod && (
        <FormField
          size="md"
          labelSize="md"
          breakpoint="md"
          layout="horizontal"
          label="Equalisation interest compounding period"
          tooltip="This is the period after which equalisation interest compounds and is considered within the basis of further equalisation interest calculations. For example, a monthly compounding period means that at the end of each month equalisation interest is added to the principal for further interest calculations, but in the middle of the month the system's daily calculation will take the principal and interest sum from the end of the previous month."
        >
          <Controller
            name={`equalisationInterestFeeTemplates.${index}.equalisationInterestCompoundingPeriod`}
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <BasicSelect
                size="small"
                options={equalisationInterestCompoundingPeriodOptions}
                placeholder="Select period"
                fullWidth
                error={!!error}
                helperText={error?.message}
                value={value ?? undefined}
                onChange={onChange}
              />
            )}
          />
        </FormField>
      )}
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Equalisation interest basis"
        tooltip="This setting determines the basis multiplied by the Equalisation interest rate above."
      >
        <Column spacing="sm">
          <Controller
            name={`equalisationInterestFeeTemplates.${index}.equalisationInterestBasis`}
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <BasicSelect
                size="small"
                options={equalisationInterestBasisOptions}
                placeholder="Select basis"
                fullWidth
                error={!!error}
                helperText={error?.message}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <Typography variant="body2">
            Equalisation interest is calculated from each capital call notice
            date.
          </Typography>
        </Column>
      </FormField>
    </Column>
  );
};

export default EqualisationInterestFeeTemplateFields;
